import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import get from 'lodash/get';
import {
  useAtomValue as useStateSegmentValue,
  useSetAtom as useStateSegmentSetter,
  useAtom as useStateSegment,
} from 'jotai';
import { emitCustomEvent } from 'react-custom-events';
import {
  RiAddFill,
  RiAiGenerate,
  RiArrowLeftDoubleFill,
  RiArrowRightDoubleFill,
  RiUpload2Line,
  // RiSettings3Line,
  RiHomeLine,
} from '@remixicon/react';
import styles from './NavigationPanel.module.css';
import {
  ACTIVE_MENU_BY_ROUTE,
  MENU_NAMES,
  ROLE,
  ANALYTICS,
  PAGE_ROUTES,
  GENERATE_CLAIMS,
  ContentTypeName,
} from '../../../constants/common';
import { trackAdminPageVisit } from '../../../services/Analytics';
import NavSection from '../../molecules/NavSection';
import PfizerLogo from '../../atoms/PfizerLogo';
import Button from '../../atoms/Button';
import { translate } from '../../../localization';
import { startNewStateSegment } from '../../../store/markettingContentGeneration/stateSegments/startNewStateSegment';
import { filtersStateSegment } from '../../../store/globalStore/stateSegments/filtersStateSegment';
import QualityCheck from '../../atoms/QualityIcon';
import GreenCircleIcon from '../../atoms/GreenCircleIcon';
import {
  GenerateContentValidationStateSegment,
  hasNeedsReview,
} from '../../../store/markettingContentGeneration/stateSegments/GenerateContentValidationStateSegment';
import { contentPaginationStateSegment } from '../../../store/markettingContentGeneration/stateSegments/contentPaginationStateSegment';
import { contentParamsSegment } from '../../../store/markettingContentGeneration/stateSegments/paramsSegment';
import {
  contentListStateSegment,
  selectedChannel,
  selectedDestinationFormat,
} from '../../../store/markettingContentGeneration/stateSegments/contentListSegment';
import Tooltip, { ToolTipSize, TooltipVariant } from '../../atoms/Tooltip';
import BookIcon from '../../atoms/BookIcon';
import { knowledgeCenterStateSegment } from '../../../store/markettingContentGeneration/stateSegments/brandsInformationStateSegment';
import { knowledgeCenterModalAtom } from '../../../store/markettingContentGeneration/stateSegments/knowledgeCenterModalStateSegment';
import ContentQualityCheck from '../ContentQualityCheck';
import ContentRuleCenterConnected from '../ContentRuleCenter/ContentRuleCenterConnected';
import ContentRuleCenterHeader from '../ContentRuleCenter/ContentRuleCenterHeader';
import ModalComponent, { ModalVariant } from '../../molecules/ModalComponent';
import {
  activeEmailScreenStateSegment,
  moduleContentsStateSegment,
} from '../../../store/markettingContentGeneration/stateSegments/emailModulesStateSegment';
import OrangeCircleIcon from '../../atoms/OrangeCircleIcon';
import {
  openPopoverStateSegment,
  // popoverStateSegment,
} from '../../../store/markettingContentGeneration/stateSegments/popoverStateSegment';
import { QcReviewName } from '../../../types/QualityChecks';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import AgenticPlannerIcon from './AgenticPlannerIcon';

interface NavigationPanelProps {
  isExpanded: boolean;
  onNavToggle: (value: boolean) => void;
  activeMenu: string;
  setActiveMenu: React.Dispatch<React.SetStateAction<string>>;
}

const NavigationPanel: FC<NavigationPanelProps> = ({
  isExpanded,
  onNavToggle,
  activeMenu,
  setActiveMenu,
}) => {
  const [knowledgeCenterModalState, setKnowledgeCenterModalState] =
    useStateSegment(knowledgeCenterModalAtom);

  const activeEmailScreen = useStateSegmentValue(activeEmailScreenStateSegment);
  // const { qualityChecksStatus } = useStateSegmentValue(popoverStateSegment);
  const currentChannel = useStateSegmentValue(selectedChannel);
  const currentDestinationFormat = useStateSegmentValue(
    selectedDestinationFormat
  );

  const openPopOver = useStateSegmentSetter(openPopoverStateSegment);

  const { isKnowledgeCenterModalOpen } = knowledgeCenterModalState;

  const [isQualityCheckModalOpen, setIsQualityCheckModalOpen] = useState(false);
  const [isContentRuleCenterModalOpen, setIsContentRuleCenterModalOpen] =
    useState(false);
  const [isOnGenerateContentRoute, setIsOnGenerateContentRoute] =
    useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const isAdminUser =
    sessionStorage.getItem('pfz-cggenai-user-role') === ROLE.ADMIN;
  const { isContentRuleBookEnabled } = useFeatureFlags();

  const { allowStartNew } = useStateSegmentValue(startNewStateSegment);
  const { action: documentAction } = useStateSegmentValue(filtersStateSegment);

  const pathname = get(location, 'pathname', '');
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  const [qcModalSize, setQcModalSize] = useState(ModalVariant.Medium);

  const showTooltip = () => setTooltipVisible(true);
  const hideTooltip = () => setTooltipVisible(false);

  const brandsInformationStateSegmentSetter = useStateSegmentSetter(
    knowledgeCenterStateSegment
  );
  const contentParams = useStateSegmentValue(contentParamsSegment);
  const emailModulesSegment = useStateSegmentValue(moduleContentsStateSegment);

  const { currentSelectedLanguage } = contentParams;

  useEffect(() => {
    if (pathname && !isKnowledgeCenterModalOpen)
      setActiveMenu(ACTIVE_MENU_BY_ROUTE[pathname]);

    // Update route state
    setIsOnGenerateContentRoute(pathname === PAGE_ROUTES.GENERATE_CONTENT);
  }, [pathname, isKnowledgeCenterModalOpen]);

  const userMenuItems = [
    {
      icon: <RiHomeLine size={24} />,
      text: translate('home'),
      name: MENU_NAMES.HOME,
      tooltip: translate('home'),
    },
    {
      icon: <RiAiGenerate size={24} />,
      text: translate('create'),
      name: MENU_NAMES.CREATE,
      tooltip: translate('create'),
    },
    {
      icon: <RiUpload2Line size={24} />,
      text: translate('upload'),
      name: MENU_NAMES.UPLOAD,
      tooltip: translate('upload_beta'),
    },
    // {
    //   icon: <RiHistoryLine size={24} />,
    //   text: translate('history'),
    //   name: MENU_NAMES.HISTORY,
    // },
  ];

  const adminMenuItems = [
    // {
    //   icon: <RiSettings3Line size={24} />,
    //   text: translate('access'),
    //   name: MENU_NAMES.ACCESS,
    //   tooltip: translate('access'),
    // },
    {
      icon: <AgenticPlannerIcon />,
      text: 'Agentic Planner',
      name: MENU_NAMES.AGENTIC_PLANNER,
      tooltip: 'Agentic Planner (Beta)',
      isBetaFeature: true,
    },
  ];

  const { contentList: contentItems } = useStateSegmentValue(
    contentListStateSegment
  );

  const currentViewVersion = useStateSegmentValue(
    contentPaginationStateSegment
  );

  const contentId = get(
    contentItems[currentViewVersion],
    `${currentSelectedLanguage}.id`,
    ''
  );

  const generateContentValidation = useStateSegmentValue(
    GenerateContentValidationStateSegment
  );

  const currentContentValidation = generateContentValidation[contentId];

  const currentContentHasError =
    currentContentValidation &&
    currentContentValidation.isLoading === false &&
    currentContentValidation.error;

  const currentContentNeedsReview =
    currentContentValidation &&
    currentContentValidation.qualityChecks &&
    currentContentValidation.qualityChecks.length &&
    !currentContentValidation.error &&
    currentContentValidation.status === QcReviewName.NEEDS_REVIEW;

  const currentContentPassed =
    currentContentValidation &&
    currentContentValidation.qualityChecks &&
    currentContentValidation.qualityChecks.length &&
    !currentContentValidation.error &&
    currentContentValidation.status === QcReviewName.PASSED;

  const isCoreMessageActive =
    currentChannel.toLowerCase() ===
      ContentTypeName.CORE_MESSAGE.toLowerCase() ||
    currentDestinationFormat.toLowerCase() ===
      ContentTypeName.CORE_MESSAGE.toLowerCase();

  const shouldShowQualityCheck = useMemo(() => {
    if (documentAction === GENERATE_CLAIMS) {
      return false;
    }
    if (isCoreMessageActive === true) {
      return false;
    }
    return true;
  }, [documentAction, isCoreMessageActive]);

  const knowledgeCenterMenuItem = {
    icon: <BookIcon />,
    text: translate('knowledge_center'),
    name: MENU_NAMES.KNOWLEDGE_CENTER,
    tooltip: translate('knowledge_center'),
  };

  const otherMenuItems = useMemo(() => {
    const qualityCheckMenuItem = {
      icon: isContentRuleBookEnabled ? (
        <QualityCheck />
      ) : (
        <div className={styles['navigation-panel-qc-wrapper']}>
          <QualityCheck />
          {isOnGenerateContentRoute &&
          (currentContentNeedsReview ||
            (activeEmailScreen === 'modules-preview' &&
              hasNeedsReview(generateContentValidation))) ? (
            <div
              className={`${
                isExpanded
                  ? styles['expanded-green-circle-icon']
                  : styles['green-circle-icon']
              }`}
            >
              <OrangeCircleIcon />
            </div>
          ) : null}
          {isOnGenerateContentRoute &&
          (currentContentPassed ||
            (activeEmailScreen === 'modules-preview' &&
              hasNeedsReview(generateContentValidation) === false)) ? (
            <div
              className={`${
                isExpanded
                  ? styles['expanded-green-circle-icon']
                  : styles['green-circle-icon']
              }`}
            >
              <GreenCircleIcon />
            </div>
          ) : null}
        </div>
      ),
      text: translate('quality_validation'),
      name: MENU_NAMES.QUALITY_VALIDATION,
      tooltip: translate('rules_center'),
    };

    return shouldShowQualityCheck
      ? [knowledgeCenterMenuItem, qualityCheckMenuItem]
      : [knowledgeCenterMenuItem];
  }, [
    isContentRuleBookEnabled,
    isOnGenerateContentRoute,
    currentContentNeedsReview,
    activeEmailScreen,
    generateContentValidation,
    isExpanded,
    currentContentPassed,
    shouldShowQualityCheck,
    knowledgeCenterMenuItem,
  ]);

  const closeQualityChecksModal = () => {
    setActiveMenu(ACTIVE_MENU_BY_ROUTE[pathname]);
    setIsQualityCheckModalOpen(false);
  };

  const closeContentRuleCenterModal = () => {
    setActiveMenu(ACTIVE_MENU_BY_ROUTE[pathname]);
    setIsContentRuleCenterModalOpen(false);
  };

  const handleCloseQualityChecksModal = () => {
    closeQualityChecksModal();
  };

  const handleQualityCheckModalSizeChange = (size: ModalVariant) => {
    setQcModalSize(size);
  };

  const handleNavMenuClicked = (name: string) => {
    if (isKnowledgeCenterModalOpen) {
      setKnowledgeCenterModalState(prev => ({
        ...prev,
        isKnowledgeCenterModalOpen: false,
      }));
    }

    if (name === 'access' && isAdminUser) {
      navigate('/user-management', {
        state: {
          isNavPanelOpen: isExpanded,
          selectedMenu: name,
        },
      });
      // track analytics for  admin page visit
      trackAdminPageVisit(ANALYTICS.ADMIN_PAGE_VISIT_NAME);
    } else {
      switch (name) {
        case 'pfizer-logo':
        case MENU_NAMES.CREATE: {
          // Check if we're already on the generate-content route
          const isNavigatingToSameRoute =
            pathname === PAGE_ROUTES.GENERATE_CONTENT;

          if (isNavigatingToSameRoute) {
            // Clear all existing search parameters
            setSearchParams(new URLSearchParams(), { replace: true });
            // Emit event to force MarketingPanel remount and clear values
            if (!(contentItems.length || emailModulesSegment.size > 0)) {
              emitCustomEvent('force-marketing-panel-remount');
            }
          } else {
            // Navigate to the route normally
            navigate(
              `${PAGE_ROUTES.GENERATE_CONTENT}${
                contentItems.length || emailModulesSegment.size > 0
                  ? '?preventSessionRestore=true'
                  : ''
              }`,
              {
                state: {
                  isNavPanelOpen: isExpanded,
                },
              }
            );
          }
          break;
        }
        case 'create-new': {
          emitCustomEvent('start-new');
          break;
        }
        case MENU_NAMES.UPLOAD: {
          navigate(PAGE_ROUTES.UPLOAD_GENERATE, {
            state: {
              isNavPanelOpen: isExpanded,
            },
          });

          break;
        }
        case MENU_NAMES.CLOSE_SIDE_BAR: {
          onNavToggle(!isExpanded);

          break;
        }
        case MENU_NAMES.HISTORY: {
          break;
        }
        case MENU_NAMES.HOME: {
          navigate(PAGE_ROUTES.HOME, {
            state: {
              isNavPanelOpen: isExpanded,
            },
          });

          break;
        }
        case MENU_NAMES.QUALITY_VALIDATION: {
          setActiveMenu(ACTIVE_MENU_BY_ROUTE[PAGE_ROUTES.QUALITY_VALIDATION]);

          // Close knowledge center modal if open
          if (isKnowledgeCenterModalOpen) {
            setKnowledgeCenterModalState(prev => ({
              ...prev,
              isKnowledgeCenterModalOpen: false,
            }));
          }

          if (currentContentHasError) {
            setIsQualityCheckModalOpen(false);
            setIsContentRuleCenterModalOpen(false);
            openPopOver();
          } else if (isContentRuleBookEnabled) {
            setIsContentRuleCenterModalOpen(!isContentRuleCenterModalOpen);
            setIsQualityCheckModalOpen(false);
          } else {
            setIsQualityCheckModalOpen(!isQualityCheckModalOpen);
            setIsContentRuleCenterModalOpen(false);
          }

          break;
        }
        case MENU_NAMES.KNOWLEDGE_CENTER: {
          // Close quality validation modals if open
          if (isQualityCheckModalOpen || isContentRuleCenterModalOpen) {
            setIsQualityCheckModalOpen(false);
            setIsContentRuleCenterModalOpen(false);
          }

          brandsInformationStateSegmentSetter(prev => {
            return {
              ...prev,
              apiPayloadUsed: {
                brand: contentParams.currentSelectedBrand,
                country: contentParams.currentSelectedCountry,
                indication: contentParams.currentSelectedIndication
                  ? [contentParams.currentSelectedIndication]
                  : [],
                targetAudience: [contentParams.currentSelectedAudience],
                language: contentParams.currentSelectedLanguage,
                topics: Array.isArray(contentParams.currentSelectedTopic)
                  ? contentParams.currentSelectedTopic
                  : [contentParams.currentSelectedTopic],
              },
            };
          });

          setKnowledgeCenterModalState(prev => ({
            ...prev,
            isKnowledgeCenterModalOpen: !isKnowledgeCenterModalOpen,
          }));
          setActiveMenu(ACTIVE_MENU_BY_ROUTE[PAGE_ROUTES.KNOWLEDGE_CENTER]);

          break;
        }

        case MENU_NAMES.AGENTIC_PLANNER: {
          window.location.href = '/agentic/index.html';
          break;
        }

        default:
          break;
      }
    }
  };

  const footerMenuItems = [
    {
      icon: isExpanded ? (
        <RiArrowLeftDoubleFill size={24} />
      ) : (
        <RiArrowRightDoubleFill size={24} />
      ),
      text: translate('close_sidebar'),
      name: MENU_NAMES.CLOSE_SIDE_BAR,
      tooltip: translate('expand_sidebar'),
    },
  ];

  useEffect(() => {
    if (currentContentHasError) {
      handleNavMenuClicked(MENU_NAMES.QUALITY_VALIDATION);
    }
  }, [currentContentHasError]);

  return (
    <nav
      className={`${styles.navbar} ${isExpanded ? '' : styles.collapsed}`}
      aria-label='Main Navigation'
      key='pf-nav-menu'
    >
      <div
        className={`${styles['brand-nav-section']} ${
          isExpanded ? '' : styles.collapsed
        }`}
      >
        <PfizerLogo
          isExpanded={isExpanded}
          onClick={handleNavMenuClicked}
          className={styles['brand-logo']}
        />
        <div onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>
          <Tooltip
            text={translate('create_new_tooltip')}
            showTooltip={allowStartNew && isTooltipVisible}
            onClickedOutside={() => setTooltipVisible(false)}
            variant={TooltipVariant.TOP_RIGHT}
            size={ToolTipSize.REGULAR}
            theme='default'
            id='create-new-tooltip'
            classes={styles.createNewBtnTooltip}
          >
            <Button
              id='create-new'
              className={`${styles['create-new']} ${
                isExpanded ? styles.expanded : styles.collapsed
              }`}
              aria-label='Create New'
              onClick={() => handleNavMenuClicked('create-new')}
              type='button'
              colorScheme='primary'
              variant='outline'
              size='lg'
              isDisabled={!allowStartNew}
            >
              <RiAddFill size={20} />
              {isExpanded && (
                <span className={styles.text}>{translate('create_new')}</span>
              )}
            </Button>
          </Tooltip>
        </div>
      </div>
      <NavSection
        items={userMenuItems}
        isExpanded={isExpanded}
        onNavMenuClicked={handleNavMenuClicked}
        className={styles['users-menu']}
        activeMenu={activeMenu}
      />
      {isAdminUser && (
        <NavSection
          items={adminMenuItems}
          isExpanded={isExpanded}
          onNavMenuClicked={handleNavMenuClicked}
          className={styles['admin-menu']}
          activeMenu={activeMenu}
        />
      )}
      <NavSection
        items={otherMenuItems}
        isExpanded={isExpanded}
        onNavMenuClicked={handleNavMenuClicked}
        className={styles['other-menu']}
        activeMenu={activeMenu}
      />
      <NavSection
        items={footerMenuItems}
        isExpanded={isExpanded}
        onNavMenuClicked={handleNavMenuClicked}
        className={styles['footer-menu']}
      />

      <ModalComponent
        isOpen={isQualityCheckModalOpen}
        onClose={() => {
          closeQualityChecksModal();
        }}
        leftOffset={isExpanded ? 260 : 80}
        variant={qcModalSize}
      >
        <ContentQualityCheck
          contentId={contentId}
          isEmailPreviewMode={activeEmailScreen === 'modules-preview'}
          isOnGenerateContentRoute={isOnGenerateContentRoute}
          onSizeChange={handleQualityCheckModalSizeChange}
          onClose={handleCloseQualityChecksModal}
        />
      </ModalComponent>

      <ModalComponent
        isOpen={isContentRuleCenterModalOpen}
        onClose={() => {
          closeContentRuleCenterModal();
        }}
        leftOffset={isExpanded ? 260 : 80}
        variant={ModalVariant.Large}
        title={<ContentRuleCenterHeader />}
      >
        <ContentRuleCenterConnected />
      </ModalComponent>
    </nav>
  );
};

NavigationPanel.displayName = 'NavigationPanel';

export default NavigationPanel;
